/**
 * Subscription API Service
 * Handles all subscription and billing API calls
 * Phase 10 Implementation
 */

import { apiClient } from './aiCrmApi';
import { ApiResponse, PaginatedResponse } from '../types';

// ===== Types =====

export interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  features: string[];
  priceMonthly: number;
  priceYearly: number;
  maxUsers?: number;
  maxLeads?: number;
  maxAIQueries?: number;
  maxEmailCampaigns?: number;
  maxStorage?: number;
  isActive: boolean;
  isRecommended: boolean;
  sortOrder: number;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  subscriptionNo: string;
  companyId: string;
  planId?: string;
  plan: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  status: 'ACTIVE' | 'SUSPENDED' | 'CANCELLED' | 'EXPIRED' | 'PAST_DUE';
  amount: number;
  currency: string;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelledAt?: string;
  cancelAtPeriodEnd: boolean;
  trialEndDate?: string;
  maxUsers?: number;
  maxLeads?: number;
  maxAIQueries?: number;
  maxEmailCampaigns?: number;
  currentUsers: number;
  currentLeads: number;
  currentAIQueries: number;
  currentEmailsSent: number;
  paymentMethod?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  autoRenew: boolean;
  subscriptionPlan?: SubscriptionPlan;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionUsage {
  users: {
    current: number;
    limit: number | null;
    percentage: number;
  };
  leads: {
    current: number;
    limit: number | null;
    percentage: number;
  };
  aiQueries: {
    current: number;
    limit: number | null;
    percentage: number;
  };
  emails: {
    current: number;
    limit: number | null;
    percentage: number;
  };
}

export interface SubscriptionInvoice {
  id: string;
  invoiceNo: string;
  subscriptionId: string;
  amount: number;
  tax: number;
  total: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'OVERDUE';
  paidAt?: string;
  paymentMethod?: string;
  lineItems: {
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }[];
  billingAddress?: {
    name?: string;
    address?: string;
    city?: string;
    country?: string;
  };
  subscription?: {
    plan: string;
    billingCycle: string;
  };
  company?: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  createdAt: string;
}

export interface LimitCheckResult {
  canProceed: boolean;
  current: number;
  limit: number | null;
  reason: string;
  suggestUpgrade: boolean;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface StripeConfigResponse {
  publishableKey: string;
}

// ===== API Functions =====

export const subscriptionApi = {
  /**
   * Get all available subscription plans
   */
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await apiClient.get<ApiResponse<SubscriptionPlan[]>>(
      '/subscriptions/plans'
    );
    return response.data.data;
  },

  /**
   * Get current subscription with usage
   */
  getCurrentSubscription: async (): Promise<{
    subscription: Subscription;
    usage: SubscriptionUsage;
  }> => {
    const response = await apiClient.get<
      ApiResponse<{ subscription: Subscription; usage: SubscriptionUsage }>
    >('/subscriptions/current');
    return response.data.data;
  },

  /**
   * Create a new subscription
   */
  createSubscription: async (input: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    paymentMethodId?: string;
  }): Promise<Subscription> => {
    const response = await apiClient.post<ApiResponse<Subscription>>(
      '/subscriptions/create',
      input
    );
    return response.data.data;
  },

  /**
   * Upgrade to a new plan
   */
  upgradePlan: async (planId: string): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      '/subscriptions/upgrade',
      { planId }
    );
    return response.data.data;
  },

  /**
   * Change billing cycle
   */
  changeBillingCycle: async (
    billingCycle: 'MONTHLY' | 'YEARLY'
  ): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      '/subscriptions/change-cycle',
      { billingCycle }
    );
    return response.data.data;
  },

  /**
   * Cancel subscription
   */
  cancelSubscription: async (immediate?: boolean): Promise<void> => {
    await apiClient.post('/subscriptions/cancel', { immediate });
  },

  /**
   * Get invoice history
   */
  getInvoices: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<SubscriptionInvoice>> => {
    const response = await apiClient.get<PaginatedResponse<SubscriptionInvoice>>(
      '/subscriptions/invoices',
      { params }
    );
    return response.data;
  },

  /**
   * Get invoice details
   */
  getInvoiceById: async (id: string): Promise<SubscriptionInvoice> => {
    const response = await apiClient.get<ApiResponse<SubscriptionInvoice>>(
      `/subscriptions/invoices/${id}`
    );
    return response.data.data;
  },

  /**
   * Pay an invoice
   */
  payInvoice: async (
    id: string,
    paymentMethodId?: string
  ): Promise<SubscriptionInvoice> => {
    const response = await apiClient.post<ApiResponse<SubscriptionInvoice>>(
      `/subscriptions/invoices/${id}/pay`,
      { paymentMethodId }
    );
    return response.data.data;
  },

  /**
   * Check if a limit is reached
   */
  checkLimit: async (
    limitType: 'leads' | 'users' | 'aiQueries' | 'emails'
  ): Promise<LimitCheckResult> => {
    const response = await apiClient.post<ApiResponse<LimitCheckResult>>(
      '/subscriptions/check-limit',
      { limitType }
    );
    return response.data.data;
  },

  // ===== Stripe Integration =====

  /**
   * Get Stripe publishable key
   */
  getStripeConfig: async (): Promise<StripeConfigResponse> => {
    const response = await apiClient.get<ApiResponse<StripeConfigResponse>>(
      '/subscriptions/stripe-config'
    );
    return response.data.data;
  },

  /**
   * Create Stripe Checkout session
   */
  createCheckoutSession: async (input: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
  }): Promise<CheckoutSessionResponse> => {
    const response = await apiClient.post<ApiResponse<CheckoutSessionResponse>>(
      '/subscriptions/create-checkout',
      input
    );
    return response.data.data;
  },

  /**
   * Create Stripe Customer Portal session
   */
  createPortalSession: async (): Promise<PortalSessionResponse> => {
    const response = await apiClient.post<ApiResponse<PortalSessionResponse>>(
      '/subscriptions/portal'
    );
    return response.data.data;
  }
};

export default subscriptionApi;
